import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Stack,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Description as DocumentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  Badge as BadgeIcon,
  AccountBalance as BankIcon,
  History as HistoryIcon,
  CheckCircleOutline as CheckIcon,
  VerifiedUser as VerifiedIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import verificationService, {
  Verification,
  GuarantorDetails,
  VerificationDetail,
  AuditLog,
} from '../services/api/verificationService';
import paymentService, { ArtisanBankAccount } from '../services/api/paymentService';
import logger from '../utils/logger';

export default function VerificationsPage() {
  const [verifications, setVerifications] = useState<Verification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [hasAttempted, setHasAttempted] = useState(false);

  // Modal states
  const [selectedVerification, setSelectedVerification] = useState<Verification | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [documentsDialog, setDocumentsDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Detail panel — full verification data fetched from /admin/verifications/{id}/
  const [detailData, setDetailData] = useState<VerificationDetail | null>(null);
  const [detailAuditLogs, setDetailAuditLogs] = useState<AuditLog[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  // Bank account state (loaded when Bank Info tab is opened)
  const [bankAccount, setBankAccount] = useState<ArtisanBankAccount | null | undefined>(undefined);
  const [bankLoading, setBankLoading] = useState(false);
  const [bankVerifying, setBankVerifying] = useState(false);

  // Load verifications only once on mount
  useEffect(() => {
    if (!hasAttempted) {
      loadVerifications();
      setHasAttempted(true);
    }
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (hasAttempted && !error) {
      setPage(0);
      loadVerifications();
    }
  }, [statusFilter, typeFilter]);

  const loadVerifications = async () => {
    try {
      setLoading(true);
      setError(null);
      setRetryCount(0);
      const data = await verificationService.getVerifications(statusFilter || undefined, typeFilter || undefined);
      setVerifications(data.verifications || []);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to load verifications. Is the backend running?';
      setError(errorMsg);
      console.error('Error loading verifications:', err);
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, any> = {
      pending: 'warning',
      pending_admin_review: 'warning',
      pending_skill_info: 'info',
      pending_identity: 'info',
      pending_guarantor: 'info',
      approved: 'success',
      rejected: 'error',
      expired: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Pending Review',
      pending_admin_review: 'Pending Review',
      pending_skill_info: 'Awaiting Skills',
      pending_identity: 'Awaiting ID',
      pending_guarantor: 'Awaiting Guarantor',
      approved: 'Approved',
      rejected: 'Rejected',
      expired: 'Expired',
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      kyc:        'KYC Verification',
      identity:   'Identity Verification',
      bank:       'Bank Account',
      selfie:     'Selfie Verification',
      guarantor:  'Guarantor Verification',
    };
    return labels[type] || type;
  };

  const handleOpenApprovalDialog = (verification: Verification) => {
    setSelectedVerification(verification);
    setActionNotes('');
    setApprovalDialog(true);
  };

  const handleOpenRejectionDialog = (verification: Verification) => {
    setSelectedVerification(verification);
    setRejectionReason('');
    setActionNotes('');
    setRejectionDialog(true);
  };

  const handleOpenDocumentsDialog = async (verification: Verification) => {
    setSelectedVerification(verification);
    setDetailData(null);
    setDetailAuditLogs([]);
    setDetailTab(0);
    setBankAccount(undefined);
    setDocumentsDialog(true);
    setDetailLoading(true);
    try {
      const res = await verificationService.getVerificationDetail(verification.id);
      setDetailData(res.verification);
      setDetailAuditLogs(res.audit_logs || []);
    } catch (err: any) {
      logger.error('Failed to load verification detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedVerification || actionLoading) return;  // ✅ Prevent double-click

    try {
      setActionLoading(true);
      logger.info('Approving verification:', selectedVerification.id);
      await verificationService.approveVerification(selectedVerification.id, actionNotes);
      logger.success('Verification approved:', selectedVerification.id);

      // ✅ Optimistic update — update both status AND raw_status so action buttons hide
      setVerifications((prev) =>
        prev.map((v) =>
          v.id === selectedVerification.id
            ? { ...v, status: 'approved' as Verification['status'], raw_status: 'approved' }
            : v
        )
      );
      setApprovalDialog(false);

      // Background refresh to sync with server
      loadVerifications().catch(() => {/* silent — optimistic update already applied */});
      alert('Verification approved successfully!');
    } catch (err: any) {
      logger.error('Approve failed:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
      alert('Failed to approve verification: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedVerification || !rejectionReason || actionLoading) {  // ✅ Prevent double-click
      if (!rejectionReason) {
        alert('Please provide a rejection reason');
      }
      return;
    }

    try {
      setActionLoading(true);
      logger.info('Rejecting verification:', selectedVerification.id);
      await verificationService.rejectVerification(
        selectedVerification.id,
        rejectionReason,
        actionNotes
      );
      logger.success('Verification rejected:', selectedVerification.id);

      // ✅ Optimistic update — update both status AND raw_status so action buttons hide
      setVerifications((prev) =>
        prev.map((v) =>
          v.id === selectedVerification.id
            ? { ...v, status: 'rejected' as Verification['status'], raw_status: 'rejected' }
            : v
        )
      );
      setRejectionDialog(false);

      // Background refresh to sync with server
      loadVerifications().catch(() => {/* silent — optimistic update already applied */});
      alert('Verification rejected successfully!');
    } catch (err: any) {
      logger.error('Reject failed:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
      alert('Failed to reject verification: ' + errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  // Filter verifications based on search query
  const filteredVerifications = verifications.filter((v) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      v.artisan.name.toLowerCase().includes(searchLower) ||
      v.artisan.phone.includes(searchQuery)
    );
  });

  const paginatedVerifications = filteredVerifications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        Verifications Management
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ p: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              size="small"
            />
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ p: 2 }}>
            <TextField
              fullWidth
              select
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter(e.target.value || null);
                setPage(0);
              }}
              size="small"
              SelectProps={{
                native: true,
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending_admin_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </TextField>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ p: 2 }}>
            <TextField
              fullWidth
              select
              value={typeFilter || ''}
              onChange={(e) => {
                setTypeFilter(e.target.value || null);
                setPage(0);
              }}
              size="small"
              SelectProps={{
                native: true,
              }}
            >
              <option value="">All Types</option>
              <option value="kyc">KYC</option>
              <option value="identity">Identity</option>
              <option value="bank">Bank</option>
              <option value="selfie">Selfie</option>
              <option value="guarantor">Guarantor</option>
            </TextField>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Verifications Table */}
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.paper', borderBottom: '2px solid #f3f4f6' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Artisan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Documents</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedVerifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No verifications found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVerifications.map((verification) => (
                    <TableRow key={verification.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {verification.artisan.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Rating: {verification.artisan.rating?.toFixed(1) || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{verification.artisan.phone}</TableCell>
                      <TableCell>{getTypeLabel(verification.type)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<DocumentIcon />}
                          onClick={() => handleOpenDocumentsDialog(verification)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                          Review Profile
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(verification.status)}
                          size="small"
                          color={getStatusColor(verification.status) as any}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(verification.submitted_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="center">
                        {verification.raw_status === 'pending_admin_review' ? (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ApproveIcon />}
                              onClick={() => handleOpenApprovalDialog(verification)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<RejectIcon />}
                              onClick={() => handleOpenRejectionDialog(verification)}
                            >
                              Reject
                            </Button>
                          </Box>
                        ) : verification.raw_status === 'approved' ? (
                          <Chip label="Approved" size="small" color="success" variant="outlined" />
                        ) : verification.raw_status === 'rejected' ? (
                          <Chip label="Rejected" size="small" color="error" variant="outlined" />
                        ) : (
                          <Chip label="Incomplete" size="small" color="default" variant="outlined"
                            title={`Artisan status: ${verification.raw_status} — not yet submitted for review`} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredVerifications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </>
      )}

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Verification</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedVerification && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Artisan:</strong> {selectedVerification.artisan.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Type:</strong> {getTypeLabel(selectedVerification.type)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Status:</strong> {selectedVerification.status}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            placeholder="Add any notes about this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Reject Verification</Typography>
          {selectedVerification && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedVerification.artisan.name} · {getTypeLabel(selectedVerification.type)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {/* Email notification banner */}
          <Alert
            severity="info"
            icon={false}
            sx={{
              mb: 2,
              background: 'linear-gradient(90deg,#eff6ff,#f0fdf4)',
              border: '1px solid #bfdbfe',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>
              📧 Automated Email Will Be Sent
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The artisan will receive an email with this rejection reason and clear steps on how to fix and resubmit.
            </Typography>
          </Alert>

          {/* Quick-pick preset reason chips */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
            QUICK SELECT A REASON
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
            {[
              'ID photo is too blurry',
              'Name on ID doesn\'t match profile',
              'Selfie doesn\'t match ID photo',
              'Document appears expired',
              'Information is incomplete',
              'Document type not accepted',
            ].map((preset) => (
              <Chip
                key={preset}
                label={preset}
                size="small"
                variant={rejectionReason === preset ? 'filled' : 'outlined'}
                color={rejectionReason === preset ? 'error' : 'default'}
                onClick={() => setRejectionReason(preset)}
                sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
              />
            ))}
          </Stack>

          <TextField
            fullWidth
            label="Rejection Reason *"
            multiline
            rows={2}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Select a reason above or type a custom one..."
            sx={{ mb: 2 }}
            helperText="This exact text will be included in the email to the artisan."
          />
          <TextField
            fullWidth
            label="Additional Guidance (Optional)"
            multiline
            rows={2}
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            placeholder="e.g. Please retake the selfie in a well-lit room facing the camera directly..."
            helperText="Extra tips to help the artisan fix and resubmit successfully."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectionDialog(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={actionLoading || !rejectionReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {actionLoading ? 'Rejecting...' : 'Reject & Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════
           VERIFICATION DETAIL DIALOG
           Three tabs: Identity · Guarantor · Timeline
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={documentsDialog} onClose={() => setDocumentsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: '1px solid #f0f0f0', pb: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>Verification Review</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                {detailData?.artisan_name || selectedVerification?.artisan.name}
                {detailData?.artisan_email ? ` · ${detailData.artisan_email}` : ''}
              </Typography>
            </Box>
            {detailData && (
              <Chip
                label={detailData.status_display || detailData.status}
                size="small"
                color={
                  detailData.status === 'approved' ? 'success' :
                  detailData.status === 'rejected' ? 'error' : 'warning'
                }
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>

          {/* ── Step completion indicator ── */}
          {detailData && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, mb: 1 }}>
              {[
                { label: 'Profile Photo', done: detailData.skill_info_completed },
                { label: 'Identity (NIN + ID)', done: detailData.identity_completed },
                { label: 'Guarantor', done: detailData.guarantor_completed },
              ].map((step, i) => (
                <Chip
                  key={i}
                  icon={step.done ? <CheckIcon style={{ fontSize: 14 }} /> : undefined}
                  label={step.label}
                  size="small"
                  variant={step.done ? 'filled' : 'outlined'}
                  color={step.done ? 'success' : 'default'}
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Stack>
          )}

          <Tabs value={detailTab} onChange={async (_, v) => {
            setDetailTab(v);
            if (v === 2 && bankAccount === undefined && selectedVerification) {
              setBankLoading(true);
              try {
                const acct = await paymentService.getArtisanBankAccount(selectedVerification.artisan.id);
                setBankAccount(acct);
              } catch { setBankAccount(null); }
              finally { setBankLoading(false); }
            }
          }} sx={{ mt: 0.5 }}>
            <Tab icon={<BadgeIcon fontSize="small" />} iconPosition="start" label="Identity" />
            <Tab icon={<PeopleIcon fontSize="small" />} iconPosition="start" label="Guarantor" />
            <Tab icon={<BankIcon fontSize="small" />} iconPosition="start" label="Bank Info" />
            <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="Timeline" />
          </Tabs>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, minHeight: 340 }}>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress size={36} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>Loading verification data…</Typography>
            </Box>
          )}

          {!detailLoading && !detailData && (
            <Alert severity="warning" sx={{ mt: 2 }}>Could not load verification details. The backend may be offline.</Alert>
          )}

          {!detailLoading && detailData && (
            <>
              {/* ══════════ TAB 0 — IDENTITY ══════════ */}
              {detailTab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                  {/* Professional photo */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      Step 1 · Professional Profile Photo
                    </Typography>
                    {detailData.professional_photo_url ? (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          component="img"
                          src={detailData.professional_photo_url}
                          alt="Profile photo"
                          sx={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 2, border: '2px solid #e0e0e0', display: 'block' }}
                          onError={(e: any) => { e.target.src = ''; }}
                        />
                        <Typography
                          component="a" href={detailData.professional_photo_url}
                          target="_blank" rel="noopener noreferrer"
                          variant="caption"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', width: 'fit-content' }}
                        >
                          <OpenIcon sx={{ fontSize: 13 }} /> Open full size
                        </Typography>
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ maxWidth: 400 }}>No profile photo uploaded yet (Step 1 incomplete).</Alert>
                    )}
                  </Box>

                  <Divider />

                  {/* NIN + ID document */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BadgeIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      Step 2 · Identity Verification
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                            NIN Number
                          </Typography>
                          <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 3, color: detailData.nin_number || detailData.nin_display ? 'text.primary' : 'text.disabled' }}>
                            {detailData.nin_number || detailData.nin_display || '— not submitted —'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">Full NIN visible to admins for manual verification</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                            ID Document Type
                          </Typography>
                          <Chip
                            label={
                              detailData.id_type === 'nin_slip' ? 'NIN Slip' :
                              detailData.id_type === 'drivers_license' ? "Driver's License" :
                              detailData.id_type === 'voters_card' ? "Voter's Card" :
                              detailData.id_type === 'passport' ? 'Passport' :
                              detailData.id_type || '— not submitted —'
                            }
                            color={detailData.id_type ? 'primary' : 'default'}
                            variant="outlined"
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Paper>
                      </Grid>
                      {detailData.id_photo_url && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                            ID Document Photo
                          </Typography>
                          <Box
                            component="img"
                            src={detailData.id_photo_url}
                            alt="ID document"
                            sx={{ width: '100%', maxHeight: 380, objectFit: 'contain', borderRadius: 2, border: '1px solid #ddd', bgcolor: '#111', display: 'block' }}
                            onError={(e: any) => { e.target.style.display = 'none'; }}
                          />
                          <Typography
                            component="a" href={detailData.id_photo_url}
                            target="_blank" rel="noopener noreferrer"
                            variant="caption"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'primary.main', textDecoration: 'underline', cursor: 'pointer', width: 'fit-content' }}
                          >
                            <OpenIcon sx={{ fontSize: 13 }} /> Open full size in new tab
                          </Typography>
                        </Grid>
                      )}
                      {!detailData.id_photo_url && (
                        <Grid item xs={12}>
                          <Alert severity="warning">No ID photo uploaded yet (Step 2 incomplete).</Alert>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Box>
              )}

              {/* ══════════ TAB 1 — GUARANTOR ══════════ */}
              {detailTab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Alert severity="info" icon={<PhoneIcon fontSize="small" />} sx={{ mb: 1 }}>
                    <strong>Admin action required:</strong> Call the guarantor to confirm they know this artisan personally and can vouch for their character and skills.
                  </Alert>

                  {!detailData.guarantor_full_name ? (
                    <Alert severity="warning">Guarantor info not submitted yet (Step 3 incomplete).</Alert>
                  ) : (
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ bgcolor: '#f0fdf4', borderBottom: '1px solid #bbf7d0', px: 2.5, py: 1.5 }}>
                        <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Vouched by
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                        {/* Name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <PersonIcon sx={{ fontSize: 20, color: '#15803d' }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Full Name</Typography>
                            <Typography variant="body1" fontWeight={700}>{detailData.guarantor_full_name}</Typography>
                          </Box>
                        </Box>

                        {/* Phone — clickable call link */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <PhoneIcon sx={{ fontSize: 20, color: '#1d4ed8' }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">Phone Number</Typography>
                            <Typography
                              component="a"
                              href={`tel:${detailData.guarantor_phone_number}`}
                              variant="body1"
                              fontWeight={600}
                              sx={{ fontFamily: 'monospace', letterSpacing: 1.5, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {detailData.guarantor_phone_number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">Click to call</Typography>
                          </Box>
                        </Box>

                        {/* Occupation + Relationship */}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <WorkIcon sx={{ fontSize: 20, color: '#a16207' }} />
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Occupation</Typography>
                                <Typography variant="body2" fontWeight={600}>{detailData.guarantor_occupation || '—'}</Typography>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <PeopleIcon sx={{ fontSize: 20, color: '#be185d' }} />
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">Relationship to Artisan</Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {detailData.guarantor_relationship
                                    ? detailData.guarantor_relationship.charAt(0).toUpperCase() + detailData.guarantor_relationship.slice(1)
                                    : '—'
                                  }
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Consent badge */}
                        <Box sx={{ pt: 1, borderTop: '1px solid #e5e7eb' }}>
                          <Chip
                            icon={<CheckIcon />}
                            label={
                              detailData.guarantor_consent
                                ? 'Artisan consented to Daily2K contacting guarantor ✓'
                                : 'Consent NOT given — do not call without permission'
                            }
                            color={detailData.guarantor_consent ? 'success' : 'error'}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  {/* Log guarantor call */}
                  <Alert severity="success" icon={<VerifiedIcon fontSize="small" />} sx={{ mt: 1 }}>
                    <strong>After calling:</strong> If the guarantor confirms, proceed to <strong>Approve</strong>. If they cannot be reached or deny knowledge, <strong>Reject</strong> with reason "Guarantor could not be verified."
                  </Alert>
                </Box>
              )}

              {/* ══════════ TAB 2 — BANK INFO ══════════ */}
              {detailTab === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                  {bankLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : bankAccount === null ? (
                    <Alert severity="warning" icon={<BankIcon fontSize="small" />}>
                      <Typography variant="body2" fontWeight={700}>No Bank Account Added Yet</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        This artisan has not linked a bank account. They will be prompted to add one when they request their first withdrawal.
                      </Typography>
                    </Alert>
                  ) : bankAccount ? (
                    <>
                      {/* Status chip */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={700}>Payout Account</Typography>
                        <Box sx={{
                          px: 1.5, py: 0.4, borderRadius: 99, fontSize: 12, fontWeight: 700,
                          bgcolor: bankAccount.payout_status === 'verified' ? '#dcfce7' : bankAccount.payout_status === 'verification_failed' ? '#fee2e2' : '#fef9c3',
                          color: bankAccount.payout_status === 'verified' ? '#166534' : bankAccount.payout_status === 'verification_failed' ? '#991b1b' : '#854d0e',
                        }}>
                          {bankAccount.payout_status === 'verified' ? '✅ Verified' : bankAccount.payout_status === 'verification_failed' ? '❌ Failed' : '⏳ Pending Verification'}
                        </Box>
                      </Box>

                      {/* Account details card */}
                      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                        <Stack spacing={1.5}>
                          {[
                            { label: 'Bank', value: bankAccount.bank_name },
                            { label: 'Account Number', value: `****${bankAccount.account_number.slice(-4)}` },
                            { label: 'Account Holder', value: bankAccount.account_holder_name },
                            { label: 'Recipient Code', value: bankAccount.paystack_recipient_code || '—' },
                            { label: 'Verified At', value: bankAccount.last_verified_at ? new Date(bankAccount.last_verified_at).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' }) : '—' },
                          ].map(({ label, value }) => (
                            <Box key={label} sx={{ display: 'flex', gap: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0 }}>{label}</Typography>
                              <Typography variant="body2" fontWeight={500}>{value}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Paper>

                      {/* Verification error */}
                      {bankAccount.verification_error && (
                        <Alert severity="error">
                          <Typography variant="body2" fontWeight={700}>Last Verification Error</Typography>
                          <Typography variant="body2">{bankAccount.verification_error}</Typography>
                        </Alert>
                      )}

                      {/* Verify button */}
                      {bankAccount.payout_status !== 'verified' && (
                        <Button
                          variant="contained"
                          color="success"
                          disabled={bankVerifying}
                          startIcon={bankVerifying ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                          onClick={async () => {
                            setBankVerifying(true);
                            try {
                              const updated = await paymentService.verifyArtisanBankAccount(selectedVerification!.artisan.id);
                              setBankAccount(updated);
                            } catch (err: any) {
                              const msg = err?.response?.data?.message || 'Verification failed';
                              setBankAccount(prev => prev ? { ...prev, payout_status: 'verification_failed', verification_error: msg } : prev);
                            } finally {
                              setBankVerifying(false);
                            }
                          }}
                          sx={{ alignSelf: 'flex-start' }}
                        >
                          {bankVerifying ? 'Verifying…' : 'Verify & Approve Payouts'}
                        </Button>
                      )}
                    </>
                  ) : null}
                </Box>
              )}

              {/* ══════════ TAB 3 — TIMELINE ══════════ */}
              {detailTab === 3 && (
                <Box sx={{ pt: 1 }}>
                  {detailAuditLogs.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No audit log entries yet.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {detailAuditLogs.map((log, idx) => (
                        <Box key={log.id} sx={{ display: 'flex', gap: 2, pb: idx < detailAuditLogs.length - 1 ? 2 : 0 }}>
                          {/* Timeline spine */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: log.action === 'approved' ? 'success.main' : log.action === 'rejected' ? 'error.main' : 'primary.main', mt: 0.6 }} />
                            {idx < detailAuditLogs.length - 1 && (
                              <Box sx={{ width: 2, flex: 1, bgcolor: '#e5e7eb', mt: 0.5 }} />
                            )}
                          </Box>
                          {/* Content */}
                          <Box sx={{ pb: 1.5 }}>
                            <Typography variant="body2" fontWeight={600}>{log.action_display}</Typography>
                            {log.notes && (
                              <Typography variant="caption" color="text.secondary" display="block">{log.notes}</Typography>
                            )}
                            <Typography variant="caption" color="text.disabled">
                              {log.performed_by_name ? `by ${log.performed_by_name} · ` : ''}
                              {new Date(log.timestamp).toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' })}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #f0f0f0', px: 3, py: 2 }}>
          <Button onClick={() => setDocumentsDialog(false)} variant="outlined">Close</Button>
          {selectedVerification && (
            ['pending', 'pending_admin_review'].includes(selectedVerification.status) ||
            Boolean(selectedVerification.raw_status && ['pending', 'pending_admin_review'].includes(selectedVerification.raw_status))
          ) && (
            <>
              <Button
                variant="contained"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => { setDocumentsDialog(false); handleOpenRejectionDialog(selectedVerification); }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => { setDocumentsDialog(false); handleOpenApprovalDialog(selectedVerification); }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
